/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, List, Button, Typography, Space, Badge, Tooltip, Spin } from 'antd';
import {
  AiOutlineFile,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineMinusCircle,
  AiOutlineUpCircle,
} from 'react-icons/ai';
import useDownload from 'hooks/useDownload';
import LocaleContext from 'locales';

const { Text } = Typography;

const CONTAINER_ID = 'download-panel-container-root';

// 다운로드 패널 컨테이너 컴포넌트
const DownloadPanelContainer = ({ children }) => {
  // 컨테이너를 위한 DOM 요소
  const containerRoot = document.getElementById(CONTAINER_ID);

  // 컨테이너 요소가 없으면 body에 추가
  useEffect(() => {
    if (!containerRoot) {
      const div = document.createElement('div');
      div.id = CONTAINER_ID;
      document.body.appendChild(div);
    }

    return () => {
      // 컴포넌트 언마운트 시 정리
      const root = document.getElementById(CONTAINER_ID);
      if (root && root.childNodes.length === 0) {
        document.body.removeChild(root);
      }
    };
  }, [containerRoot]);

  return createPortal(children, containerRoot || document.body);
};

const DownloadProgressPanel = () => {
  const { locale } = useContext(LocaleContext);
  const [bottomPosition, setBottomPosition] = useState('20px');

  const {
    items: downloadItems,
    status: downloadStatus,
    messages: downloadMessages,
    visible,
    collapsed,
    toggleCollapsed,
    clearCompleted,
    cancelDownload,
  } = useDownload();

  // 항목별 카테고리 분류
  const activeItems = downloadItems.filter((item) =>
    ['preparing', 'downloading', 'compressing'].includes(downloadStatus[item.uid]),
  );

  const completedItems = downloadItems.filter((item) => downloadStatus[item.uid] === 'completed');

  const failedItems = downloadItems.filter((item) =>
    ['error', 'cancelled'].includes(downloadStatus[item.uid]),
  );

  const activeCount = activeItems.length;
  const completedCount = completedItems.length;
  const failedCount = failedItems.length;
  const totalCount = downloadItems.length;

  // 업로드 패널 위치 조정
  useEffect(() => {
    if (!visible || downloadItems.length === 0) return;

    const checkUploadPanel = () => {
      const uploadPanel = document.querySelector('.upload-progress-panel');
      if (!uploadPanel) {
        setBottomPosition('20px');
        return;
      }

      const rect = uploadPanel.getBoundingClientRect();
      if (rect.height > 0) {
        // 업로드 패널 상단에 배치
        const newPosition = window.innerHeight - rect.top + 5;
        setBottomPosition(`${newPosition}px`);
      } else {
        setBottomPosition('20px');
      }
    };

    checkUploadPanel();

    // 변화 감지를 위한 MutationObserver 설정
    const observer = new MutationObserver(checkUploadPanel);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [visible, downloadItems.length]);

  // 다운로드 항목이 없으면 패널을 표시하지 않음
  if (!visible || downloadItems.length === 0) {
    return null;
  }

  // 패널 닫기
  const handleClose = () => {
    if (activeCount > 0) {
      // 진행 중인 다운로드가 있으면 패널 최소화
      toggleCollapsed(true);
    } else {
      // 완료된 항목만 있으면 정리하고 패널 닫기
      clearCompleted();
    }
  };

  const renderHeader = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Space>
        <span>{locale.resource['txt.file-transfer.file-download']}</span>
        {activeCount > 0 && <Badge count={activeCount} style={{ backgroundColor: '#1890ff' }} />}
      </Space>
      <Space>
        {!collapsed && (
          <Button
            type="text"
            size="large"
            icon={<AiOutlineMinusCircle />}
            onClick={() => toggleCollapsed(true)}
          />
        )}
        {collapsed && (
          <Button
            type="text"
            size="large"
            icon={<AiOutlineUpCircle />}
            onClick={() => toggleCollapsed(false)}
          />
        )}
        {activeCount === 0 && (
          <Button type="text" size="large" icon={<AiOutlineCloseCircle />} onClick={handleClose} />
        )}
      </Space>
    </div>
  );

  const renderDownloadItem = (item) => {
    const status = downloadStatus[item.uid];
    const message = downloadMessages[item.uid] || '';

    const isCompleted = status === 'completed';
    const isDownloading = status === 'downloading';
    const isPreparing = status === 'preparing';
    const isCompressing = status === 'compressing';
    const isReady = status === 'ready';
    const isCancelled = status === 'cancelled';
    const isError = status === 'error';

    const actions = [];

    if (isDownloading || isPreparing || isCompressing) {
      actions.push(
        <Tooltip key="cancel" title={locale.resource['txt.file-transfer.cancel']}>
          <Button
            type="text"
            size="large"
            danger
            onClick={() => cancelDownload(item.uid)}
            icon={<AiOutlineCloseCircle />}
          />
        </Tooltip>,
      );
    }

    if (isCompleted) {
      actions.push(
        <Tooltip key="completed" title={locale.resource['txt.file-transfer.completed']}>
          <div style={{ display: 'inline-flex', cursor: 'default' }}>
            <AiOutlineCheckCircle
              style={{
                fontSize: 18,
                color: '#52c41a',
                pointerEvents: 'none',
              }}
            />
          </div>
        </Tooltip>,
      );
    }

    // 상태에 따른 메시지 표시 처리
    let displayMessage = '';
    if (isPreparing) {
      displayMessage = locale.resource['txt.file-transfer.download-preparing']; // "다운로드 준비 중입니다."
    } else if (isCompressing) {
      displayMessage = locale.resource['txt.file-transfer.download-preparing']; // "다운로드 준비 중입니다."
    } else if (isReady || isDownloading) {
      displayMessage = locale.resource['txt.file-transfer.download-ready']; // "다운로드 준비가 완료되었습니다."
    } else if (isCancelled) {
      displayMessage = locale.resource['txt.file-transfer.status-cancelled'];
    } else if (isError) {
      displayMessage = locale.resource['txt.file-transfer.status-error'];
    } else if (isCompleted) {
      displayMessage = locale.resource['txt.file-transfer.status-completed'];
    }

    return (
      <List.Item
        key={item.uid}
        style={{ height: 50, display: 'flex', alignItems: 'center' }}
        actions={actions}
      >
        <List.Item.Meta
          avatar={<AiOutlineFile style={{ fontSize: 18 }} />}
          title={
            <Text
              type={isError ? 'danger' : 'secondary'}
              style={{ fontSize: '12px', display: 'block' }}
            >
              {displayMessage}
            </Text>
          }
        />
        {/* {(isDownloading || isPreparing || isCompressing) && <Spin size="small" />} */}
      </List.Item>
    );
  };

  // 축소된 상태
  const renderCollapsedContent = () => (
    <div style={{ padding: '8px 12px' }}>
      <Text style={{ fontSize: '12px' }}>
        {completedCount}/{totalCount} {locale.resource['txt.file-transfer.completed']}
        {activeCount > 0 &&
          `, ${activeCount} ${locale.resource['txt.file-transfer.progress-in-progress']}`}
        {failedCount > 0 &&
          `, ${failedCount} ${locale.resource['txt.file-transfer.progress-failed']}`}
      </Text>
    </div>
  );

  // 확장
  const renderExpandedContent = () => (
    <>
      <div className="download-overall-status" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {completedCount}/{totalCount}{' '}
            {locale.resource['txt.file-transfer.progress-files-complete']}
          </Text>
        </div>
      </div>

      {downloadItems.length > 0 && (
        <div className="download-items">
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            {locale.resource['txt.file-transfer.download-items']} ({downloadItems.length})
          </Text>
          <List size="small" dataSource={downloadItems} renderItem={renderDownloadItem} />
        </div>
      )}
    </>
  );

  return (
    <DownloadPanelContainer>
      <div
        className="download-progress-panel"
        style={{
          position: 'fixed',
          bottom: bottomPosition,
          right: '20px',
          width: collapsed ? '200px' : '380px',
          maxHeight: '500px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          transition: 'width 0.3s ease, bottom 0.3s ease',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Card
          title={renderHeader()}
          bodyStyle={{
            padding: collapsed ? '8px' : '12px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {collapsed ? renderCollapsedContent() : renderExpandedContent()}
        </Card>
      </div>
    </DownloadPanelContainer>
  );
};

export default DownloadProgressPanel;
